import type { PublicSkill } from "@/lib/api-client";
import type { LibraryItem } from "@/lib/mock/library";

const SKILL_ACCENT = "#ffffff";

/** Friendly short labels for the most common raw data-source ids. */
const SOURCE_LABELS: Record<string, string> = {
  stock_price_bars_with_indicators: "Price Bars",
  index_price_bars_with_indicators: "Index Bars",
  options_flow_analysis: "Options Flow",
  options_flow_alerts: "Flow Alerts",
  options_flow_trades: "Flow Trades",
  recent_flows: "Recent Flows",
  top_net_premiums: "Net Premiums",
  where_are_the_gamma_walls: "Gamma Walls",
  options_gex_spot_strikes: "GEX",
  options_gex_by_strike: "GEX",
  dealer_exposure: "Dealer Exposure",
  volatility_assessment: "Volatility",
  volatility_percentiles: "Vol Percentiles",
  volatility_smile: "Vol Smile",
  variance_risk_premium: "Variance Premium",
  iv_rank: "IV Rank",
  iv_term_structure: "Term Structure",
  interpolated_iv: "Interpolated IV",
  implied_moves_monthly: "Implied Move",
  company_snapshot: "Company",
  quote: "Quote",
  stock_quotes: "Quote",
  financial_ratios: "Ratios",
  financial_scores: "Scores",
  key_metrics: "Key Metrics",
  income_statement: "Income Stmt",
  dcf: "DCF",
  price_target: "Price Target",
  price_target_consensus: "Price Target",
  earnings_analysis: "Earnings",
  earnings_history: "Earnings",
  earnings_price_action: "Earnings Moves",
  earnings_financials: "Earnings Fins",
  earnings_screener: "Earnings Screen",
  quality_compounder_check: "Quality",
  is_it_overvalued: "Valuation",
  drawdown_analysis: "Drawdown",
  stock_risk_snapshot: "Risk",
  analyst_ratings: "Analyst",
  stock_grades_consensus: "Grades",
  short_interest: "Short Interest",
  short_interest_top_shorts: "Top Shorts",
  borrow_rates: "Borrow Rates",
  ftds: "FTDs",
  is_this_a_squeeze_setup: "Squeeze",
  politician_recent_trades: "Congress",
  politician_trades: "Congress",
  insider_trades: "Insider",
  insider_trading_latest: "Insider",
  institution_activity: "Institutional",
  institutional_activity: "Institutional",
  institutional_ownership_latest: "Ownership",
  dark_pool: "Dark Pool",
  lit_trades: "Lit Trades",
  market_tide: "Market Tide",
  sector_market_tide: "Sector Tide",
  market_sentiment: "Sentiment",
  market_mood: "Market Mood",
  macro_regime: "Macro",
  fred_economic_data: "Macro Data",
  fund_agent: "SEC Filings",
  fmp_fetch: "Fundamentals",
  news_search: "News",
  news_by_ticker: "News",
  twitter_search: "Twitter",
  reddit_search: "Reddit",
  etf_heatmap: "ETF Heatmap",
  sector_heatmap_options: "Sector Heat",
  futures_indexes: "Futures",
  futures_snapshot: "Futures",
  index_snapshot: "Indexes",
  fda_calendar: "FDA Calendar",
  trading_calendar: "Calendar",
  seasonality: "Seasonality",
  dividends: "Dividends",
  options_chain: "Options Chain",
  options_oi_changes: "OI Changes",
  options_contract_oi_changes: "OI Changes",
  contract_interval_flow: "Contract Flow",
  options_ticker_interval_flow: "Ticker Flow",
  volume_price_levels: "Volume Levels",
  stocks_general: "Screener",
  stocks: "Screener",
  stocks_options: "Options Screen",
};

export function prettySource(id: string): string {
  if (SOURCE_LABELS[id]) return SOURCE_LABELS[id];
  return id
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .split(" ")
    .slice(0, 2)
    .join(" ");
}

/** Strip markdown syntax so the card preview reads like clean paragraphs. */
export function cleanInstruction(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/^[\s]*[-*>]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/[*_>#]/g, "")
    .replace(/\n{2,}/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}


/** Headline runtime label for the card: prefer the doc label, then the
 *  recommended model's seconds, then any model with a runtime. */
export function skillRuntimeLabel(s: PublicSkill): string {
  if (s.est_runtime_label) return s.est_runtime_label;
  const me = s.model_estimates || {};
  const rec = s.recommended_model || "flash";
  const secs =
    me[rec]?.est_runtime_seconds ??
    s.est_runtime_seconds ??
    me.flash?.est_runtime_seconds ??
    me.opus?.est_runtime_seconds;
  if (!secs) return "";
  const min = Math.round(secs / 60);
  return min <= 1 ? "~1 min" : `~${min} min`;
}

export function skillToLibraryItem(s: PublicSkill): LibraryItem {
  // Preview/blurb come from the VISIBLE explanation, never the hidden
  // engineered agent_instruction. cleanInstruction just strips markdown so it
  // reads as flowing paragraphs in the tiny zoomed-out card preview.
  const explanation = s.explanation || "";
  // Prefer the dedicated short `description` for the blurb (strip markdown so
  // the tiny zoomed-out card reads as plain text). Fall back to the cleaned
  // explanation for docs not yet regenerated.
  const blurbSource =
    (s.description && s.description.trim()) || explanation || s.agent_instruction || "";
  const cleaned = cleanInstruction(blurbSource);
  const cleanedExplanation = cleanInstruction(explanation || s.agent_instruction || "");

  // Real per-model prices from model_estimates (fixes the base*6 inflation).
  const me = s.model_estimates || {};
  const flashPrice = me.flash?.est_cost_usd;
  const opusPrice = me.opus?.est_cost_usd;
  // Fallback base only if model_estimates is missing entirely.
  const basePrice = flashPrice ?? (s.est_cost_usd > 0 ? s.est_cost_usd : 0.3);

  return {
    id: s.id,
    kind: "skill",
    title: s.name,
    blurb: cleaned.slice(0, 220),
    meta: "",
    accent: SKILL_ACCENT,
    topic: "",
    basePrice,
    flashPrice,
    opusPrice,
    recommendedModel: s.recommended_model,
    thumbnailUrl: s.thumbnail_url,
    sources: s.data_sources.length,
    uses: s.usage_count,
    runtimeLabel: skillRuntimeLabel(s),
    tags: s.data_sources.map(prettySource),
    previewMd: cleanedExplanation.slice(0, 600),
  };
}
